import { request } from '@/lib/api'

/**
 * Исходящая почта с карточки и шаблоны писем (тикет #117).
 *
 * Поля ответа сравниваются нестрого (`== null`): сервер не кладёт в JSON пустые поля вовсе
 * (`non_null`), и `undefined` здесь — обычное «не заполнено», а не поломка.
 */

/** Записи, с карточки которых пишется письмо; совпадает с типом ленты. */
export type MailRecordType = 'deal' | 'counterparty' | 'contact' | 'invoice'

/** Для чего написан шаблон: `any` — для любой карточки. */
export type MailAnchor = 'any' | MailRecordType

export interface MailTemplate {
  id: string
  version: number
  name: string
  anchor: MailAnchor
  subject: string
  body: string
  active: boolean
}

export interface MailVariable {
  name: string
  anchors: MailAnchor[]
}

export interface MailRecipient {
  contactId?: string | null
  name?: string | null
  email: string
}

export interface MailDraftAttachment {
  id: string
  fileName: string
  sizeBytes: number
  invoicePdf: boolean
  available: boolean
  /** `pending`, `infected`, `failed` — вердикт антивируса; `missing` — файла нет в хранилище. */
  unavailableReason?: string | null
}

export interface MailDraft {
  subject: string
  body: string
  /** Переменные шаблона, которым нечего подставить: в тексте на их месте пусто. */
  missing: string[]
  recipients: MailRecipient[]
  attachments: MailDraftAttachment[]
  /** Почта подключена: иначе письмо запишется в ленту с отказом, а не уйдёт. */
  mailConnected: boolean
}

export type MailStatus = 'pending' | 'sending' | 'sent' | 'failed' | 'rejected'

export interface MailMessage {
  id: string
  activityId: string
  entityType: MailRecordType
  entityId: string
  to: string
  subject: string
  status: MailStatus
  attempts: number
  reason?: string | null
  retryAfter?: string | null
  sentAt?: string | null
}

export interface MailConnection {
  enabled: boolean
  host?: string | null
  port?: number | null
  security: 'none' | 'starttls' | 'ssl'
  username?: string | null
  passwordSet: boolean
  fromAddress?: string | null
  fromName?: string | null
}

export interface MailConnectionUpdate {
  enabled: boolean
  host: string | null
  port: number | null
  security: MailConnection['security']
  username: string | null
  /** `undefined` — оставить сохранённый, пустая строка — стереть. */
  password?: string
  fromAddress: string | null
  fromName: string | null
}

export interface SendMailRequest {
  entityType: MailRecordType
  entityId: string
  templateId?: string | null
  to: string
  subject: string
  body: string
  attachmentIds: string[]
  /** Входящее письмо, на которое это ответ (#134): уйдёт с In-Reply-To и ляжет в его тред. */
  replyToInboundId?: string | null
  /** Черновик ИИ, с которого начат текст (#134): сервер запишет, правил ли его менеджер. */
  draftTaskId?: string | null
}

/** Куда уходит ответ на входящее письмо: карточка письма, отправитель, «Re:» темы (#134). */
export interface ReplyTarget {
  inboundId: string
  entityType: MailRecordType
  entityId: string
  to?: string | null
  subject: string
}

export function fetchReplyTarget(inboundId: string, signal?: AbortSignal): Promise<ReplyTarget> {
  return request<ReplyTarget>(`/mail/inbound/${inboundId}/reply`, { signal })
}

export function fetchTemplatesFor(entityType: MailRecordType, signal?: AbortSignal): Promise<MailTemplate[]> {
  return request<{ templates: MailTemplate[] }>(`/mail/templates?entityType=${entityType}`, { signal })
    .then((body) => body.templates ?? [])
}

export function composeMail(entityType: MailRecordType, entityId: string, templateId: string | null,
                            signal?: AbortSignal): Promise<MailDraft> {
  return request<MailDraft>('/mail/compose', {
    method: 'POST',
    body: { entityType, entityId, templateId },
    signal,
  })
}

/**
 * Отправка. Ключ идемпотентности рождается при открытии формы и живёт до её закрытия:
 * двойной клик или потерянный ответ повторяют тот же запрос, и сервер отдаёт сохранённый
 * ответ вместо второго письма.
 */
export function sendMail(body: SendMailRequest, idempotencyKey: string): Promise<MailMessage> {
  return request<MailMessage>('/mail/send', { method: 'POST', body, idempotencyKey })
}

export function retryMail(messageId: string): Promise<MailMessage> {
  return request<MailMessage>(`/mail/messages/${messageId}/retry`, { method: 'POST', body: {} })
}

export function fetchMailTemplates(signal?: AbortSignal): Promise<{ templates: MailTemplate[]; variables: MailVariable[] }> {
  return request<{ templates?: MailTemplate[]; variables?: MailVariable[] }>('/admin/email-templates', { signal })
    .then((body) => ({ templates: body.templates ?? [], variables: body.variables ?? [] }))
}

export interface MailTemplateFields {
  name: string
  anchor: MailAnchor
  subject: string
  body: string
  active: boolean
}

export function createMailTemplate(fields: MailTemplateFields): Promise<MailTemplate> {
  return request<MailTemplate>('/admin/email-templates', { method: 'POST', body: fields })
}

export function updateMailTemplate(id: string, fields: Partial<MailTemplateFields>): Promise<MailTemplate> {
  return request<MailTemplate>(`/admin/email-templates/${id}`, { method: 'PUT', body: fields })
}

export function deleteMailTemplate(id: string): Promise<void> {
  return request<void>(`/admin/email-templates/${id}`, { method: 'DELETE' })
}

export function fetchMailConnection(signal?: AbortSignal): Promise<MailConnection> {
  return request<MailConnection>('/admin/integrations/email', { signal })
}

export function updateMailConnection(update: MailConnectionUpdate): Promise<MailConnection> {
  return request<MailConnection>('/admin/integrations/email', { method: 'PUT', body: update })
}
